import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, RefreshCw, Lock, Eye, Play, Loader2 } from 'lucide-react';

const DeveloperProcessed = () => {
  const { organizationId } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';
  const [sessions, setSessions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [jsonUnlocked, setJsonUnlocked] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const perPage = 20;

  useEffect(() => {
    if (!organizationId) return;
    loadSessions();
  }, [organizationId, page]);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('sessions')
        .select('id, session_id, tutor_name, student_name, lesson_timestamp, duration_minutes, subject_covered, processed_at, analysis_json, lesson_summary, student_engagement_level, risk_level')
        .eq('organization_id', organizationId)
        .eq('is_processed_v2', true)
        .not('analysis_json', 'is', null)
        .order('processed_at', { ascending: false })
        .range(page * perPage, (page + 1) * perPage - 1);
      setSessions(data || []);
    } catch (err) {
      console.error('Error loading processed sessions:', err);
    }
    setLoading(false);
  };

  const riskColor = (level) => {
    const l = (level || '').toLowerCase();
    if (l === 'high' || l === 'urgent') return 'text-red-400 bg-red-400/10';
    if (l === 'medium' || l === 'moderate') return 'text-amber-400 bg-amber-400/10';
    if (l === 'low' || l === 'minor') return 'text-yellow-400 bg-yellow-400/10';
    return 'text-emerald-400 bg-emerald-400/10';
  };

  const engColor = (eng) => {
    const e = (eng || '').toLowerCase();
    if (e === 'high') return 'text-emerald-400';
    if (e === 'medium') return 'text-amber-400';
    if (e === 'low') return 'text-red-400';
    return isDark ? 'text-[#52525b]' : 'text-[#999]';
  };

  const cardClass = `rounded-lg border ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate('/developer')} className={`flex items-center gap-2 text-sm ${isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666] hover:text-[#1a1a1a]'}`}>
        <ArrowLeft className="h-4 w-4" /> Back to Developer
      </button>

      <div className="flex items-center justify-between">
        <h1 className={`text-2xl font-semibold font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
          Processed Sessions
        </h1>
        <button onClick={loadSessions} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-[#18181b] text-[#a1a1aa]' : 'hover:bg-[#f0f0f0] text-[#666]'}`}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Two-panel */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* List */}
        <div className={`${cardClass} lg:col-span-2 overflow-hidden`}>
          <div className={`max-h-[650px] overflow-y-auto divide-y ${isDark ? 'divide-[#27272a]' : 'divide-[#e5e5e5]'}`}>
            {loading ? (
              <div className={`p-8 text-center text-sm ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Loading...</div>
            ) : sessions.map(s => (
              <button
                key={s.id}
                onClick={() => setSelected(s)}
                className={`w-full px-4 py-3 text-left transition-colors ${
                  selected?.id === s.id
                    ? isDark ? 'bg-[#18181b]' : 'bg-[#f5f5f5]'
                    : isDark ? 'hover:bg-[#18181b]/50' : 'hover:bg-[#fafafa]'
                }`}
              >
                <div className="flex items-center justify-between mb-0.5">
                  <span className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{s.tutor_name}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${riskColor(s.risk_level)}`}>
                    {s.risk_level || 'Clear'}
                  </span>
                </div>
                <div className={`text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{s.student_name} · {s.subject_covered || 'N/A'}</div>
                <div className={`text-xs mt-0.5 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                  {s.lesson_timestamp ? new Date(s.lesson_timestamp).toLocaleDateString([], { day: 'numeric', month: 'short' }) : '--'}
                  {s.processed_at && ` · Processed ${new Date(s.processed_at).toLocaleDateString([], { day: 'numeric', month: 'short' })}`}
                </div>
              </button>
            ))}
          </div>
          {/* Pagination */}
          <div className={`flex items-center justify-center gap-2 p-3 border-t ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
            <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="p-1 disabled:opacity-30">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className={`text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Page {page + 1}</span>
            <button onClick={() => setPage(page + 1)} disabled={sessions.length < perPage} className="p-1 disabled:opacity-30">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Detail */}
        <div className={`${cardClass} lg:col-span-3 p-5`}>
          {!selected ? (
            <div className={`text-center py-16 text-sm ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Select a session to view analysis</div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
                  {selected.tutor_name} → {selected.student_name}
                </h3>
                <button
                  onClick={async () => {
                    const code = prompt('Enter developer code to reprocess:');
                    if (code !== '240501') return;
                    setReprocessing(true);
                    try {
                      await supabase.functions.invoke('process-transcript-full', { body: { sessionId: selected.id } });
                    } catch (err) { console.error('Reprocess error:', err); }
                    setReprocessing(false);
                  }}
                  disabled={reprocessing}
                  className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${isDark ? 'text-[#52525b] hover:text-[#a1a1aa] hover:bg-[#18181b]' : 'text-[#999] hover:text-[#666] hover:bg-[#f0f0f0]'}`}
                >
                  {reprocessing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />} Reprocess
                </button>
              </div>

              <div className={`grid grid-cols-3 gap-3 text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                <div>
                  <span className={isDark ? 'text-[#52525b]' : 'text-[#999]'}>Risk Level</span>
                  <div className={`font-medium mt-0.5 ${riskColor(selected.risk_level).split(' ')[0]}`}>{selected.risk_level || 'Clear'}</div>
                </div>
                <div>
                  <span className={isDark ? 'text-[#52525b]' : 'text-[#999]'}>Engagement</span>
                  <div className={`font-medium mt-0.5 ${engColor(selected.student_engagement_level)}`}>{selected.student_engagement_level || 'N/A'}</div>
                </div>
                <div>
                  <span className={isDark ? 'text-[#52525b]' : 'text-[#999]'}>Duration</span>
                  <div className="font-medium mt-0.5">{selected.duration_minutes || '?'}m</div>
                </div>
              </div>

              {/* Summary */}
              {selected.lesson_summary && (
                <div>
                  <span className={`text-xs font-medium ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Lesson Summary</span>
                  <p className={`text-sm mt-1 leading-relaxed ${isDark ? 'text-[#a1a1aa]' : 'text-[#555]'}`}>{selected.lesson_summary}</p>
                </div>
              )}

              {/* Analysis JSON */}
              <div className={`border-t pt-3 ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs font-medium ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Analysis JSON</span>
                  {!jsonUnlocked && (
                    <button
                      onClick={() => {
                        const code = prompt('Enter developer code:');
                        if (code === '240501') setJsonUnlocked(true);
                      }}
                      className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${isDark ? 'text-[#52525b] hover:text-[#a1a1aa] hover:bg-[#18181b]' : 'text-[#999] hover:text-[#666] hover:bg-[#f0f0f0]'}`}
                    >
                      <Lock className="h-3 w-3" /> Unlock
                    </button>
                  )}
                </div>
                {jsonUnlocked ? (
                  <pre className={`p-3 rounded-lg text-xs max-h-64 overflow-y-auto overflow-x-auto ${isDark ? 'bg-[#18181b] text-[#a1a1aa]' : 'bg-[#f5f5f5] text-[#555]'}`}>
                    {JSON.stringify(selected.analysis_json, null, 2).slice(0, 5000)}
                  </pre>
                ) : (
                  <div className={`flex items-center justify-center gap-2 py-8 rounded-lg ${isDark ? 'bg-[#18181b]' : 'bg-[#f5f5f5]'}`}>
                    <Lock className={`h-4 w-4 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} />
                    <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Enter developer code to view</span>
                  </div>
                )}
              </div>

              {/* Timestamps */}
              <div className={`text-xs space-y-0.5 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                <div>Lesson: {selected.lesson_timestamp ? new Date(selected.lesson_timestamp).toLocaleString() : '--'}</div>
                <div>Processed: {selected.processed_at ? new Date(selected.processed_at).toLocaleString() : '--'}</div>
                <div>Session ID: <code className={isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}>{selected.session_id || selected.id}</code></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeveloperProcessed;
